/**
 * PermissionContext — fetches the current user's per-page permissions from
 * `GET /api/me/permissions` and exposes a small API for nav filtering,
 * route guards and read-only mode.
 *
 *   const { permissions, isBuiltinAdmin, can, isReady } = usePermissions();
 *   if (!can("admin_capacity", "edit")) return <ReadOnlyBanner/>;
 *
 * Levels:
 *   - null (or missing)  → no access
 *   - "view"             → read-only
 *   - "edit"             → full access
 *
 * Built-in admin roles (platform_admin, operations_manager, holding_admin)
 * always have "edit" on every page — the server enforces this too.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PermissionContext = createContext(null);

export const usePermissions = () => {
  const ctx = useContext(PermissionContext);
  if (!ctx) throw new Error("usePermissions must be used within PermissionProvider");
  return ctx;
};

export const PermissionProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [state, setState] = useState({
    permissions: {},        // {page_key: "view" | "edit"}
    isBuiltinAdmin: false,
    email: "",
    role: "",
    ready: false,           // true once the first fetch has completed
    error: null,
  });

  const fetchPerms = useCallback(async () => {
    // Users invited via the Doserne whitelist authenticate through DOSERNE_TOKEN
    // (no scaffoldx_token). Their permissions still live in `page_permissions`
    // and must be honoured by the main-platform admin pages. Fall back to
    // whichever token is present.
    const doserneToken = (() => {
      try { return localStorage.getItem("DOSERNE_TOKEN"); } catch { return null; }
    })();
    const anyToken = token || doserneToken;
    if (!anyToken) {
      setState({ permissions: {}, isBuiltinAdmin: false, email: "", role: "", ready: true, error: null });
      return;
    }
    try {
      const { data } = await axios.get(`${API}/me/permissions`, {
        headers: { Authorization: `Bearer ${anyToken}` },
      });
      setState({
        permissions: data.permissions || {},
        isBuiltinAdmin: !!data.is_builtin_admin,
        email: data.email || "",
        role: data.role || "",
        ready: true,
        error: null,
      });
    } catch (e) {
      // 401/403 → no permissions. Fail closed but do not spam console.
      setState({ permissions: {}, isBuiltinAdmin: false, email: "", role: "", ready: true, error: e });
    }
  }, [token, user]);

  useEffect(() => { fetchPerms(); }, [fetchPerms]);

  const can = useCallback((pageKey, level = "view") => {
    if (!state.ready) return false;
    if (state.isBuiltinAdmin) return true;
    const lvl = state.permissions?.[pageKey];
    if (!lvl) return false;
    if (level === "edit") return lvl === "edit";
    return lvl === "view" || lvl === "edit";
  }, [state]);

  const value = useMemo(() => ({
    ...state,
    can,
    refresh: fetchPerms,
  }), [state, can, fetchPerms]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

/** Route guard component. Redirects to fallback (or shows message) when the
 *  user lacks the required access level. Renders children otherwise. */
export const PermissionGate = ({ pageKey, level = "view", fallback = null, children }) => {
  const { can, ready } = usePermissions();
  if (!ready) return null; // avoid flash while loading
  if (!can(pageKey, level)) {
    return fallback || (
      <div className="min-h-[40vh] flex items-center justify-center text-slate-400 text-sm">
        <div className="text-center">
          <div className="text-4xl mb-3">🔒</div>
          <div className="font-black uppercase tracking-widest text-slate-300 mb-1">Access denied</div>
          <div className="text-slate-500">You do not have {level} permission for this page.</div>
        </div>
      </div>
    );
  }
  return children;
};
